/* global AbortController */
import { Controller } from "@hotwired/stimulus"

// Deliberately self-contained rather than sharing the signup form's helpers, to keep the two able
// to change independently.
//
// Not wired through the shared `validation` controller: this form gates on password strength plus
// confirmation match, and `isFieldValid` only ever inspects one field at a time, so it cannot
// compare two against each other. The error timing differs too - both "invalid password" and
// "passwords don't match" arm on focusout with no dirty gate and no focusin-clear.
//
// The strength checklist itself belongs to password_requirements_controller.js; this controller
// only reads the `.satisfied` classes it sets.
export default class extends Controller {
  // Every listener below is bound here rather than declared as a data-action, so a reconnect on
  // the same form would bind a second copy of each - and each copy carries its own error <span>,
  // so a single blur would report the same problem twice. The controller is created per connect,
  // never in initialize, so a reconnect gets a fresh one rather than an already-aborted one.
  connect() {
    this.abortController = new AbortController();

    this.passwordInput = this.element.querySelector(".user_password .form-control");
    this.confirmationInput = this.element.querySelector(".user_password_confirmation .form-control");
    this.confirmationIcon = this.element.querySelector(".user_password_confirmation .input-addon.addon-append i");
    this.submitButton = this.element.querySelector(".edit-password-submit .btn");
    this.inputs = Array.from(this.element.querySelectorAll(".form-control"));

    this.setInvalidPasswordHandler();
    this.setInvalidPasswordConfirmationHandler();
    this.enableFormValidation();
  }

  disconnect() {
    this.abortController.abort();
  }

  togglePasswordInput() {
    if (this.confirmationInput.type === "password") {
      this.confirmationInput.type = "text";
      this.confirmationIcon.classList.remove("icon-lasso-show");
      this.confirmationIcon.classList.add("icon-lasso-hide");
    } else {
      this.confirmationInput.type = "password";
      this.confirmationIcon.classList.remove("icon-lasso-hide");
      this.confirmationIcon.classList.add("icon-lasso-show");
    }
  }

  passwordMeetsCriteria() {
    return this.element.querySelectorAll(".password-requirement .satisfied").length === 4;
  }

  passwordsMatch() {
    const passwords = this.element.querySelectorAll(".form-control.password");
    if (passwords.length < 2) { return true; }

    return passwords[0].value === passwords[1].value;
  }

  setInvalidPasswordHandler() {
    const errorMessage = document.createElement("span");
    errorMessage.classList.add("help-block");
    errorMessage.textContent = "invalid password";

    this.passwordInput.addEventListener("focusout", () => {
      const formGroup = this.passwordInput.closest(".form-group");

      if (!this.passwordMeetsCriteria()) {
        formGroup.classList.add("has-error");
        formGroup.appendChild(errorMessage);
      } else {
        formGroup.classList.remove("has-error");
        errorMessage.remove();
      }
    }, { signal: this.abortController.signal });
  }

  setInvalidPasswordConfirmationHandler() {
    const errorMessage = document.createElement("span");
    errorMessage.classList.add("help-block");
    errorMessage.textContent = "passwords don't match";

    this.confirmationInput.addEventListener("focusout", () => {
      const formGroup = this.confirmationInput.closest(".form-group");

      if (!this.passwordsMatch()) {
        formGroup.classList.add("has-error");
        formGroup.appendChild(errorMessage);
      } else {
        formGroup.classList.remove("has-error");
        errorMessage.remove();
      }
    }, { signal: this.abortController.signal });
  }

  validateForm(values) {
    if (values.includes("")) { return false; }

    return this.passwordMeetsCriteria() && this.passwordsMatch();
  }

  enableFormValidation() {
    this.inputs.forEach((input) => {
      input.addEventListener("input", () => {
        const values = this.inputs.map((i) => i.value);
        this.submitButton.disabled = !this.validateForm(values);
      }, { signal: this.abortController.signal });
    });
  }
}
